import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import { initGA, trackPageView } from './utils/analytics';
import useScrollPosition from './hooks/useScrollPosition';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import OurStory from './pages/OurStory';
import Timeline from './pages/Timeline';
import Gallery from './pages/Gallery';
import Messages from './pages/Messages';
import Medical from './pages/Medical';
import Resources from './pages/Resources';
import Gratitude from './pages/Gratitude';
import ElizasAward from './pages/ElizasAward';
import Spiritual from './pages/Spiritual';
import MediaRecognition from './pages/MediaRecognition';
import AdminDashboard from './pages/AdminDashboard';
import AdminAnalytics from './pages/AdminAnalytics';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Georgia', serif;
    line-height: 1.6;
    color: #333;
    background-color: #f8f9fa;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Arial', sans-serif;
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 1rem;
  }

  a {
    color: #2c5282;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const theme = {
  colors: {
    primary: '#2c5282',
    secondary: '#3182ce',
    accent: '#e53e3e',
    success: '#38a169',
    warning: '#d69e2e',
    light: '#f7fafc',
    dark: '#2d3748',
    gray: '#718096'
  },
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    desktop: '1200px'
  }
};

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  width: 100%;
`;

const MainContent = styled.main`
  flex: 1;
  padding: 0;
  padding-top: ${props => props.$isScrolled ? '80px' : '0'};
  transition: padding-top 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding-top: ${props => props.$isScrolled ? '70px' : '0'};
  }
`;

// Analytics page tracking component
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track page view when location changes
    trackPageView(location.pathname);
  }, [location]);

  return null;
}

// Layout wrapper to handle scroll state
function AppLayout() {
  const { isScrolled } = useScrollPosition();

  return (
    <AppContainer>
      <Header />
      <MainContent $isScrolled={isScrolled}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/story" element={<OurStory />} />
          <Route path="/timeline" element={<Timeline />} />
          <Route path="/media-recognition" element={<MediaRecognition />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/videos" element={<Navigate to="/gallery" replace />} />
          <Route path="/gratitude" element={<Gratitude />} />
          <Route path="/elizas-award" element={<ElizasAward />} />
          <Route path="/medical" element={<Medical />} />
          <Route path="/spiritual" element={<Spiritual />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
        </Routes>
      </MainContent>
      <Footer />
    </AppContainer>
  );
}

function App() {
  useEffect(() => {
    // Initialize Google Analytics on app start
    initGA();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <Router future={{ v7_relativeSplatPath: true }}>
        <AnalyticsTracker />
        <AppLayout />
      </Router>
    </ThemeProvider>
  );
}

export default App;
